import { useEffect, useCallback } from 'react';
import { useNavigate } from '@tanstack/react-router';
import { Check } from 'lucide-react';
import { LandingTopNav } from '../LandingTopNav';
import { AudienceFooter } from '../audience/AudienceFooter';
import { boardsStore, generateBoardId } from '../../stores/boardsStore';
import { getEmployeesArray } from '../../employees/employeesStore';
import { useSeoMeta } from '../../hooks/useSeoMeta';

/* ── Types ───────────────────────────────────── */

type Tier = {
  name: string;
  badge?: string;
  oldPrice: string;
  tagline: string;
  features: string[];
  cta: string;
  highlighted?: boolean;
};

type FaqItem = { q: string; a: string };

type PricingContent = {
  title: string;
  metaDesc: string;
  hero: { h1: string; sub: string };
  tiers: [Tier, Tier, Tier];
  faq: { heading: string; items: FaqItem[] };
  cta: { heading: string; body: string; board: string; canvas: string };
};

/* ── Content ─────────────────────────────────── */

const content: Record<'de' | 'en', PricingContent> = {
  de: {
    title: 'No Pricing — qtplan',
    metaDesc: 'qtplan ist kostenlos. Keine versteckten Kosten, kein Abo, kein Account.',
    hero: {
      h1: 'No Pricing',
      sub: 'Drei Pläne. Ein Preis. Null Euro.',
    },
    tiers: [
      {
        name: 'Hobby',
        oldPrice: '0€',
        tagline: 'Kostenlos. Wirklich.',
        features: ['Unlimited Boards', 'Unlimited Canvas', 'PERT-Schätzungen', 'Gantt-Timeline', 'Squad-Forming', 'Skill-Matrix', 'Konfidenzintervalle', 'Lokale Daten'],
        cta: 'Kostenlos starten',
      },
      {
        name: 'Pro',
        badge: 'Beliebteste',
        oldPrice: '0€',
        tagline: 'Identisch mit Hobby. Aber klingt besser.',
        features: ['Unlimited Boards', 'Unlimited Canvas', 'PERT-Schätzungen', 'Gantt-Timeline', 'Squad-Forming', 'Skill-Matrix', 'Konfidenzintervalle', 'Lokale Daten'],
        cta: 'Auch kostenlos',
        highlighted: true,
      },
      {
        name: 'Enterprise',
        oldPrice: '0€',
        tagline: 'Kein Sales-Team das Sie anruft.',
        features: ['Unlimited Boards', 'Unlimited Canvas', 'PERT-Schätzungen', 'Gantt-Timeline', 'Squad-Forming', 'Skill-Matrix', 'Konfidenzintervalle', 'Lokale Daten'],
        cta: 'Immer noch kostenlos',
      },
    ],
    faq: {
      heading: 'Häufig gestellte Fragen',
      items: [
        { q: 'Gibt es versteckte Kosten?', a: 'Nein. Es gibt nicht mal sichtbare Kosten.' },
        { q: 'Was passiert nach der Testphase?', a: 'Nichts. Es gibt keine Testphase. Es ist einfach kostenlos.' },
        { q: 'Wo ist der Haken?', a: 'Alle Daten bleiben lokal im Browser. Kein Backend, kein Account, kein Abo. Der Haken ist dass wir kein Geld damit verdienen.' },
      ],
    },
    cta: {
      heading: 'Überzeugt?',
      body: 'Direkt loslegen — ohne Registrierung.',
      board: 'Board erstellen',
      canvas: 'Canvas erstellen',
    },
  },
  en: {
    title: 'No Pricing — qtplan',
    metaDesc: 'qtplan is free. No hidden costs, no subscriptions, no account required.',
    hero: {
      h1: 'No Pricing',
      sub: 'Three plans. One price. Zero euros.',
    },
    tiers: [
      {
        name: 'Hobby',
        oldPrice: '0€',
        tagline: 'Free. Really.',
        features: ['Unlimited Boards', 'Unlimited Canvas', 'PERT Estimates', 'Gantt Timeline', 'Squad Forming', 'Skill Matrix', 'Confidence Intervals', 'Local Data'],
        cta: 'Start for free',
      },
      {
        name: 'Pro',
        badge: 'Most Popular',
        oldPrice: '0€',
        tagline: 'Identical to Hobby. But sounds better.',
        features: ['Unlimited Boards', 'Unlimited Canvas', 'PERT Estimates', 'Gantt Timeline', 'Squad Forming', 'Skill Matrix', 'Confidence Intervals', 'Local Data'],
        cta: 'Also free',
        highlighted: true,
      },
      {
        name: 'Enterprise',
        oldPrice: '0€',
        tagline: 'No sales team will call you.',
        features: ['Unlimited Boards', 'Unlimited Canvas', 'PERT Estimates', 'Gantt Timeline', 'Squad Forming', 'Skill Matrix', 'Confidence Intervals', 'Local Data'],
        cta: 'Still free',
      },
    ],
    faq: {
      heading: 'Frequently Asked Questions',
      items: [
        { q: 'Are there hidden costs?', a: 'No. There aren\'t even visible costs.' },
        { q: 'What happens after the trial?', a: 'Nothing. There is no trial. It\'s just free.' },
        { q: 'What\'s the catch?', a: 'All data stays local in your browser. No backend, no account, no subscription. The catch is that we don\'t make any money from it.' },
      ],
    },
    cta: {
      heading: 'Convinced?',
      body: 'Get started right away — no sign-up required.',
      board: 'Create Board',
      canvas: 'Create Canvas',
    },
  },
};

/* ── Component ───────────────────────────────── */

export function NoPricingPage({ lang }: { lang: 'de' | 'en' }) {
  const navigate = useNavigate();
  const c = content[lang];

  const otherLang = lang === 'de' ? 'en' : 'de';
  useSeoMeta({
    title: c.title,
    description: c.metaDesc,
    lang,
    path: `/${lang}/pricing`,
    alternatePath: `/${otherLang}/pricing`,
    jsonLd: {
      '@type': 'FAQPage',
      mainEntity: c.faq.items.map((item) => ({
        '@type': 'Question',
        name: item.q,
        acceptedAnswer: { '@type': 'Answer', text: item.a },
      })),
    },
  });

  useEffect(() => { window.scrollTo(0, 0); }, [lang]);

  const goToHome = useCallback(() => { window.scrollTo(0, 0); navigate({ to: '/' }); }, [navigate]);
  const goToBoards = useCallback(() => { window.scrollTo(0, 0); navigate({ to: '/boards' }); }, [navigate]);
  const goToCanvas = useCallback(() => {
    const now = new Date().toISOString();
    const today = `${new Date().getFullYear()}-${String(new Date().getMonth() + 1).padStart(2, '0')}-${String(new Date().getDate()).padStart(2, '0')}`;
    const boardId = generateBoardId('canvas');
    boardsStore.send({
      type: 'board.create',
      board: { id: boardId, name: 'Canvas', startDate: today, createdAt: now, updatedAt: now, employeeIds: getEmployeesArray().map(e => e.id) },
    });
    window.scrollTo(0, 0);
    navigate({ to: '/boards/$boardId/canvas', params: { boardId } });
  }, [navigate]);

  return (
    <div className="landing-root">
      <LandingTopNav
        onScrollToFeatures={goToHome}
        onScrollToDemo={goToHome}
        onCreateBoard={goToBoards}
        onCreateCanvas={goToCanvas}
      />

      <article>
        {/* ── Hero ── */}
        <section className="audience-hero">
          <h1>{c.hero.h1}</h1>
          <p className="audience-hero-lead">{c.hero.sub}</p>
        </section>

        {/* ── Pricing Grid ── */}
        <section style={{ padding: '0 24px 96px', maxWidth: 1080, margin: '0 auto' }}>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 24 }}>
            {c.tiers.map((tier) => (
              <div
                key={tier.name}
                style={{
                  background: 'var(--landing-surface)',
                  border: tier.highlighted ? '2px solid var(--landing-accent)' : '1px solid var(--landing-border)',
                  borderRadius: 12,
                  padding: 32,
                  display: 'flex',
                  flexDirection: 'column',
                }}
              >
                <div style={{ marginBottom: 24 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 16 }}>
                    <span style={{ fontSize: 18, fontWeight: 600, color: 'var(--landing-text-1)' }}>{tier.name}</span>
                    {tier.badge && (
                      <span style={{ background: 'var(--landing-accent)', color: '#fff', fontSize: 10, fontWeight: 600, padding: '2px 8px', borderRadius: 9999, whiteSpace: 'nowrap' }}>
                        {tier.badge}
                      </span>
                    )}
                  </div>
                  <div style={{ color: 'var(--landing-text-4)', textDecoration: 'line-through', fontSize: 14, marginBottom: 4 }}>{tier.oldPrice}</div>
                  <div style={{ fontSize: 40, fontWeight: 700, color: 'var(--landing-text-1)', lineHeight: 1.1 }}>0€</div>
                  <p style={{ fontSize: 14, color: 'var(--landing-text-3)', marginTop: 8 }}>{tier.tagline}</p>
                </div>
                <ul style={{ listStyle: 'none', padding: 0, margin: 0, flex: 1, display: 'flex', flexDirection: 'column', gap: 10 }}>
                  {tier.features.map((feat) => (
                    <li key={feat} style={{ display: 'flex', alignItems: 'flex-start', gap: 8, fontSize: 14 }}>
                      <Check size={16} style={{ color: 'var(--landing-accent)', marginTop: 2, flexShrink: 0 }} />
                      <span>{feat}</span>
                    </li>
                  ))}
                </ul>
                <button
                  onClick={goToBoards}
                  style={{
                    marginTop: 24, width: '100%', padding: '10px 16px', borderRadius: 8, border: 'none',
                    fontSize: 14, fontWeight: 500, cursor: 'pointer',
                    background: tier.highlighted ? 'var(--landing-accent)' : 'hsla(0,0%,100%,0.08)',
                    color: '#fff', transition: 'opacity 0.15s',
                  }}
                  onMouseEnter={e => (e.currentTarget.style.opacity = '0.85')}
                  onMouseLeave={e => (e.currentTarget.style.opacity = '1')}
                >
                  {tier.cta}
                </button>
              </div>
            ))}
          </div>
        </section>

        {/* ── FAQ ── */}
        <section style={{ padding: '0 24px 96px', maxWidth: 720, margin: '0 auto' }}>
          <h2 style={{ fontSize: 24, fontWeight: 600, color: 'var(--landing-text-1)', textAlign: 'center', marginBottom: 48 }}>
            {c.faq.heading}
          </h2>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 24 }}>
            {c.faq.items.map((item) => (
              <div key={item.q} style={{ border: '1px solid var(--landing-border)', borderRadius: 12, padding: 24, background: 'var(--landing-surface)' }}>
                <h3 style={{ fontWeight: 500, color: 'var(--landing-text-1)', marginBottom: 8, fontSize: 16 }}>{item.q}</h3>
                <p style={{ fontSize: 14, color: 'var(--landing-text-2)', margin: 0, lineHeight: 1.6 }}>{item.a}</p>
              </div>
            ))}
          </div>
        </section>

        {/* ── CTA ── */}
        <section className="audience-cta-section">
          <h2>{c.cta.heading}</h2>
          <p>{c.cta.body}</p>
          <div className="audience-cta-actions">
            <button className="landing-btn-primary" onClick={goToBoards}>{c.cta.board}</button>
            <button className="landing-btn-primary" onClick={goToCanvas}>{c.cta.canvas}</button>
          </div>
        </section>
      </article>

      <AudienceFooter lang={lang} currentSlug="pricing" basePath="pricing" />
    </div>
  );
}
